import React from 'react';
import { Text, View, StyleSheet } from '@react-pdf/renderer';
import { PDF_COLORS } from '../pdfColors';
import { PDFRecordTable } from '../types';

import { pdfSafeText } from '../pdfSafeText';

const styles = StyleSheet.create({
  table: {
    width: '100%',
    marginTop: 4,
  },
  headerRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: PDF_COLORS.greyMid,
    paddingVertical: 3,
    paddingHorizontal: 3,
  },
  row: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: PDF_COLORS.greyMid,
    paddingVertical: 3,
    paddingHorizontal: 3,
  },
  rowAlt: {
    backgroundColor: PDF_COLORS.greyLight,
  },
  th: {
    fontSize: 7,
    fontFamily: 'Poppins',
    fontWeight: 600,
    color: PDF_COLORS.ink,
    paddingHorizontal: 3,
  },
  td: {
    fontSize: 7,
    fontFamily: 'Poppins',
    color: PDF_COLORS.ink,
    paddingHorizontal: 3,
  },
});

interface Props {
  table: PDFRecordTable;
  /**
   * Repite la fila de cabecera en cada página cuando la tabla se parte.
   *
   * OJO: `fixed` de react-pdf pinta el elemento en TODAS las páginas del
   * `<Page>` que lo contiene, no solo mientras la tabla fluye. Actívalo solo
   * en tablas que ocupan su propia página; si la tabla comparte página con
   * otro contenido, la cabecera aparecería también sobre ese otro contenido.
   */
  repeatHeader?: boolean;
}

/**
 * Tabla simple para react-pdf: fila de cabecera + filas de datos, anchos por
 * columna según `colFlex` y alineación opcional por columna (`align`). Zebra en
 * filas impares. Usada para el detalle de OTs por operario/empresa en el PDF de
 * Gestión Horaria y otros listados tabulares.
 */
export const PDFTable = ({ table, repeatHeader = false }: Props) => {
  const flexOf = (i: number) => table.colFlex?.[i] ?? 1;
  const alignOf = (i: number) => table.align?.[i] ?? 'left';
  return (
    <View style={styles.table}>
      <View style={styles.headerRow} fixed={repeatHeader}>
        {table.headers.map((h, i) => (
          <Text
            key={i}
            style={[styles.th, { flex: flexOf(i), textAlign: alignOf(i) }]}
          >
            {pdfSafeText(h)}
          </Text>
        ))}
      </View>
      {table.rows.map((row, ri) => (
        <View
          key={ri}
          style={[styles.row, ri % 2 === 1 ? styles.rowAlt : {}]}
          wrap={false}
        >
          {row.map((cell, ci) => (
            <Text
              key={ci}
              style={[styles.td, { flex: flexOf(ci), textAlign: alignOf(ci) }]}
            >
              {pdfSafeText(cell)}
            </Text>
          ))}
        </View>
      ))}
    </View>
  );
};
